import React, {useState} from 'react'
import {Modal, Button} from 'react-bootstrap'
import {KTSVG} from '../../../../../_metronic/helpers'
import clsx from 'clsx'
import styles from './QuickFiltersModal.module.css'

interface TeamMember {
  _id: string
  name: string
  email: string
  avatar?: string
}

interface FilterState {
  searchType: string | null;
  assignedTo: string | null
  // Current system filters only
  messageFilter:
    | 'All Message'
    | 'My Message'
    | 'Active Messages Only'
    | 'Archived'
    | 'Flagged'
    | 'Unread'
    | 'Opened'
    | 'Subscribed'
    | 'Unsubscribed'
    | null;
  sortOrder?: 'descend' | 'ascend';
}

interface QuickFiltersModalProps {
  show: boolean
  onHide: () => void
  onApplyFilters: (filters: FilterState) => void
  onClearFilters: () => void
  teamMembers: TeamMember[]
  currentFilters: FilterState
}

const QuickFiltersModal: React.FC<QuickFiltersModalProps> = ({
  show,
  onHide,
  onApplyFilters,
  onClearFilters,
  teamMembers,
  currentFilters,
}) => {
  const [filters, setFilters] = useState<FilterState>({
    ...currentFilters,
    sortOrder: currentFilters.sortOrder || 'descend',
  })

  const handleApply = () => {
    onApplyFilters(filters)
    onHide()
  }

  const handleClear = () => {
    const clearedFilters: FilterState = {
      assignedTo: null,
      messageFilter: null,
      searchType: 'by-filter',
      sortOrder: 'descend',
    }
    setFilters(clearedFilters)
    onClearFilters()
  }

  const FilterButton: React.FC<{
    active: boolean
    onClick: () => void
    children: React.ReactNode
  }> = ({active, onClick, children}) => (
    <button
      className={clsx('btn btn-sm me-2 mb-2', active ? 'btn-primary' : 'btn-light-primary')}
      onClick={onClick}
    >
      {active}
      {children}
    </button>
  )

  return (
    <Modal show={show} onHide={onHide} size='lg' centered>
      <Modal.Header className={styles.modalHeader}>
        <Modal.Title className='fs-4 fw-bold'>Quick Filters</Modal.Title>
        <button className='btn btn-sm btn-light-primary' onClick={handleClear}>
          <KTSVG path='/media/icons/duotune/arrows/arr088.svg' className='svg-icon-3 me-1' />
          Clear Filters
        </button>
      </Modal.Header>

      {/* Currently Applied Filters */}
      {(currentFilters.assignedTo || currentFilters.messageFilter) && (
        <div className={clsx('px-4 py-2 bg-light border-bottom', styles.appliedFilters)}>
          <div className='d-flex align-items-center gap-2 flex-wrap'>
            <span className='text-muted fs-8 fw-bold'>Currently Applied:</span>
            {currentFilters.assignedTo && (
              <span className='badge badge-light-primary'>
                Assigned:{' '}
                {currentFilters.assignedTo === 'my-chat'
                  ? 'My Chat'
                  : currentFilters.assignedTo === 'unassigned'
                  ? 'Unassigned'
                  : teamMembers.find((m) => m._id === currentFilters.assignedTo)?.name ||
                    'Team Member'}
              </span>
            )}
            {currentFilters.messageFilter && (
              <span className='badge badge-light-info'>Filter: {currentFilters.messageFilter}</span>
            )}
          </div>
        </div>
      )}

      <Modal.Body className={styles.modalBody}>
        {/* Sort By Newest/Oldest */}
        <div className={styles.filterSection}>
          <h6 className={styles.filterTitle}>Sort By</h6>
          <div className='d-flex flex-wrap'>
            <FilterButton
              active={filters.sortOrder === 'descend' || !filters.sortOrder}
              onClick={() => setFilters(prev => ({ ...prev, sortOrder: 'descend' }))}
            >
              Newest
            </FilterButton>
            <FilterButton
              active={filters.sortOrder === 'ascend'}
              onClick={() => setFilters(prev => ({ ...prev, sortOrder: 'ascend' }))}
            >
              Oldest
            </FilterButton>
          </div>
        </div>
        {/* Search (Contact) */}
        <div className={styles.filterSection}>
          <h6 className={styles.filterTitle}>Search Mode</h6>
          <div className='d-flex flex-wrap'>
            <FilterButton
              active={filters.searchType === 'all'}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  searchType: prev.searchType === 'all' ? null : 'all',
                }))
              }
            >
              All
            </FilterButton>
            <FilterButton
              active={filters.searchType === 'by-filter'}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  searchType: prev.searchType === 'by-filter' ? null : 'by-filter',
                }))
              }
            >
              By Filter
            </FilterButton>
          </div>
        </div>

        {/* Assigned (Contact Owner) */}
        <div className={styles.filterSection}>
          <h6 className={styles.filterTitle}>Assigned (Contact Owner)</h6>
          <div className='d-flex flex-wrap'>
            <FilterButton
              active={filters.assignedTo === 'my-chat'}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  assignedTo: prev.assignedTo === 'my-chat' ? null : 'my-chat',
                  messageFilter: prev.messageFilter === 'My Message' ? null : 'My Message',
                }))
              }
            >
              My Chat
            </FilterButton>
            <FilterButton
              active={filters.assignedTo === 'unassigned'}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  assignedTo: prev.assignedTo === 'unassigned' ? null : 'unassigned',
                }))
              }
            >
              Unassigned
            </FilterButton>
          </div>

          {/* Team Members */}
          {teamMembers.length > 0 && (
            <div className='mt-3'>
              <div className={styles.filterTitle}>Filter by team member:</div>
              <div className='d-flex flex-wrap'>
                {teamMembers.map((member) => (
                  <FilterButton
                    key={member?._id}
                    active={filters.assignedTo === member?._id}
                    onClick={() =>
                      setFilters((prev) => ({
                        ...prev,
                        assignedTo: prev.assignedTo === member?._id ? null : member?._id,
                      }))
                    }
                  >
                    {member?.name}
                  </FilterButton>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Current System Filters */}
        <div className={styles.filterSection}>
          <h6 className={styles.filterTitle}>Message Filters</h6>
          <div className='d-flex flex-wrap'>
            <FilterButton
              active={filters.messageFilter === 'All Message'}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  messageFilter: prev.messageFilter === 'All Message' ? null : 'All Message',
                }))
              }
            >
              All Messages
            </FilterButton>
            <FilterButton
              active={filters.messageFilter === 'Active Messages Only'}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  messageFilter: prev.messageFilter === 'Active Messages Only' ? null : 'Active Messages Only',
                }))
              }
            >
              Active Messages Only
            </FilterButton>
            <FilterButton
              active={filters.messageFilter === 'Flagged'}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  messageFilter: prev.messageFilter === 'Flagged' ? null : 'Flagged',
                }))
              }
            >
              Flagged
            </FilterButton>
            <FilterButton
              active={filters.messageFilter === 'Archived'}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  messageFilter: prev.messageFilter === 'Archived' ? null : 'Archived',
                }))
              }
            >
              Archived
            </FilterButton>
            <FilterButton
              active={filters.messageFilter === 'Unread'}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  messageFilter: prev.messageFilter === 'Unread' ? null : 'Unread',
                }))
              }
            >
              Unread Messages
            </FilterButton>
            <FilterButton
              active={filters.messageFilter === 'Opened'}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  messageFilter: prev.messageFilter === 'Opened' ? null : 'Opened',
                }))
              }
            >
              Opened
            </FilterButton>
            <FilterButton
              active={filters.messageFilter === 'Subscribed'}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  messageFilter: prev.messageFilter === 'Subscribed' ? null : 'Subscribed',
                }))
              }
            >
              Subscribed
            </FilterButton>
            <FilterButton
              active={filters.messageFilter === 'Unsubscribed'}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  messageFilter: prev.messageFilter === 'Unsubscribed' ? null : 'Unsubscribed',
                }))
              }
            >
              Unsubscribed
            </FilterButton>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className={styles.modalFooter}>
        <Button variant='light' onClick={onHide}>
          Cancel
        </Button>
        <Button variant='primary' onClick={handleApply}>
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default QuickFiltersModal
